import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import scalesJusticeImage from '@/assets/scales-justice.jpg';
import { useEffect, useRef, useState } from 'react';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg text-muted-foreground leading-relaxed text-justify transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

const About = () => {

  return (
    <section id="about" className="pt-32 pb-20 bg-background">
      <div className="max-w-6xl mx-auto px-6">
        <div>
          <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-8 text-center">
            Welcome to Freeman & Crown<br className="hidden md:block" /> Equity Law Group
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <AnimatedParagraph delay={200}>
              Freeman & Crown Equity Law Group has been established to eradicate the ignorance that enslaves the men and women of the land, and to—without apology—enforce the law of natural rights with honor and dignity.
            </AnimatedParagraph>
            <AnimatedParagraph delay={400}>
              Our authority is rooted first and foremost in the natural, immutable laws of existence—the divine order often called God's Law—strengthened by disciplined overstanding of what the original Constitution of the United States of America Republic of 1776 and the Bill of Rights is meant to be.
            </AnimatedParagraph>
            <AnimatedParagraph delay={600}>
              It is from this union of divine principle and foundational law that Freeman & Crown generates powerful processes and solutions to the most challenging legal issues.
            </AnimatedParagraph>
          </div>
          
          <div className="my-12 mx-auto max-w-2xl px-8 py-6 border-l-4 border-gold bg-gold/5 rounded-r-lg">
            <blockquote className="text-2xl md:text-3xl font-bold text-foreground leading-tight italic text-center">
              "The greatest power requires<br />the lightest touch."
            </blockquote>
            
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <AnimatedParagraph delay={1200}>
              We understand the immense power of Natural Law and the sovereign authority it bestows upon those who walk in harmony with its truth.
            </AnimatedParagraph>
            <AnimatedParagraph delay={1400}>
              Our purpose is fixed—not only in obedience to Nature's wisdom—but in reverence to the divine intention of the original constitutional principles of self-governance, freedom, and personal dominion.  It is important to realize that we also do recognize that the original constitution was written for white men, but save that one flaw, these documents represent true sovereignty under the laws of the most high creator.  We recognize that there is no explicit mention of the Constitution being explicitly for white people. It was just that the writers of those documents did not see other people as people, which we all know is bullshit,  therefore, we don't go by what was intended  by the fallacy and deviousness of those who wrote the documents. We go by what was written down. And those documents were written for all people of the Republic. We recognize these documents as sacred instruments of right and remedy, and we invoke them accordingly.
            </AnimatedParagraph>
            <AnimatedParagraph delay={1600}>
              We stand in unflinching defense of the rights of all men and women of the Republic—who conduct their lives in accordance with the natural order—ensuring that our freedom remains secured and shielded against unlawful interference.
            </AnimatedParagraph>
            <AnimatedParagraph delay={1800}>
              Our commitment to truth and service is unwavering. Every process we employ, every solution we provide, and every method we refine is offered with the aim of restoring sovereignty to men and women of the republic. Our work is ever-evolving—sharpened by research, shaped by experience, and guided by wisdom.
            </AnimatedParagraph>
            <AnimatedParagraph delay={2000}>
              Freeman & Crown Equity Law Group is itself a living body—constantly expanding, refining, and aligning with whatever is the truth. We add as we go. We improve. We discard anything that does not serve truth and freedom. This dynamic process is reflected in every portal, platform, and publication we make available.
            </AnimatedParagraph>
            <AnimatedParagraph delay={2200}>
              Through our work, we ensure that every sincere truth-seeker has access to the very same lawful tools we use to live freely—so that any man or woman may rise and stand in full authority, ready to defend all that is right, just, and true.
            </AnimatedParagraph>
            <AnimatedParagraph delay={2400}>
              Above all we stand as guardians of truth and justice—firm and relentless—in defense, and offense of the sacred and unalienable rights granted by Divine Providence. These rights form the authentic and rightful foundation of our Republic.
            </AnimatedParagraph>
          </div>

          {/* Mission Statement */}
          <div className="mt-16 p-8 bg-gradient-to-r from-primary/5 to-gold/5 rounded-lg border border-gold/20">
            <h3 className="font-rockwell text-2xl font-semibold text-primary mb-6 text-center">
              Our Commitment to the Men and Women of the Land
            </h3>
            <blockquote className="text-lg italic text-muted-foreground mb-6 leading-relaxed text-center border-l-4 border-gold pl-4">
              "The Constitution is not a document for the government to give rights to the people, 
              but a charter to prevent government and anyone else from infringing upon your God-Given inalienable rights."
            </blockquote>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed text-justify">
              We are committed to being defenders of natural rights, providing truthful and lawful guidance while upholding abundance mentality. 
              Through full transparency and openness, we offer completely free resources to empower all men and women of the land in their pursuit of sovereignty and freedom.
            </p>
            <div className="text-center flex flex-col md:flex-row gap-4 justify-center items-center">
              <Button asChild className="bg-foreground hover:bg-foreground/90 text-background font-semibold px-8 py-3 w-full md:w-48">
                <Link to="/work-with-us">
                  Work with us
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild className="bg-foreground hover:bg-foreground/90 text-background px-8 py-3 w-full md:w-48">
                <Link to="/education">
                  Free Resources
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Trustee Section */}
          <div className="mt-32 border-t border-gold/20 pt-16">
            {/* Trustee Header */}
            <div className="text-center mb-16">
              <h2 className="font-rockwell text-3xl md:text-4xl font-semibold text-primary mb-8">
                Trustees of the Freeman & Crown<br />Equity Law Group
              </h2>
            </div>
            
            {/* Mobile layout */}
            <div className="lg:hidden">
              <div className="mb-8">
                <img 
                  src="/lovable-uploads/15868f15-6c06-4358-98e2-9fc153ff4e2c.png" 
                  alt="Adé Abioyé - Trustee" 
                  className="w-full aspect-[3/4] object-cover rounded-lg shadow-lg"
                />
              </div>
              
              <div className="text-left mb-8">
                <div className="mb-2">
                  <p className="text-lg text-muted-foreground mb-1">His Excellency,</p>
                  <h3 className="font-rockwell text-3xl text-primary font-semibold">Prince Adé</h3>
                </div>
                <p className="text-lg text-muted-foreground font-medium">
                  Attorney-in-fact • Trustee
                </p>
              </div>
              
              <div className="max-w-4xl mx-auto">
                <div className="space-y-6">
                  <div>
                    <div className="my-8 space-y-6">
                      <AnimatedParagraph delay={300}>
                        For years after my so-called awakening, I committed what I now consider to be one of the gravest of betrayals:
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={400}>
                        I stayed silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={500}>
                        I had seen with my own eyes— very clearly— the reality that was strategically hidden from the eyes of the masses, yet I stayed silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={600}>
                        I claimed allegiance to the truth, and yet I remained silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={700}>
                        I said I'm a free man of the land and freedom is the birthright of all my people.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={800}>
                        And yet, I remained silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={900}>
                        And in my silence—I became complicit.
                      </AnimatedParagraph>
                    </div>
                  </div>
                  
                  <AnimatedParagraph delay={1000}>
                    I allowed fear to grip my throat and smother the truth that clawed to be set free. Though my eyes had pierced the veil of deception, I remained tethered to fragments of the very illusion I condemned. Bound by the remnants of the programming and the whispers of self-doubt.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1100}>
                    For whatever reason, I let conformity poison my convictions.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1200}>
                    Imagine claiming to have the keys to freedom, only to voluntarily stay locked within the cage of cultural hypnosis—afraid of the responses I might receive from the very hypnotized that I was called to to bring awareness to.
                  </AnimatedParagraph>
                  
                  {/* First Pull Quote */}
                  <div>
                    <div className="my-12 mx-8 md:mx-24 lg:mx-32 px-8 py-6 border-l-4 border-gold bg-gold/5 rounded-r-lg">
                      <blockquote className="text-2xl md:text-3xl font-bold text-foreground leading-tight italic">
                        "Every day I chose not to speak—was an act of treason—not just against my conscience, but against the divine contract that is written in the deepest part of me."
                      </blockquote>
                    </div>
                  </div>
                  
                  <AnimatedParagraph delay={1400}>
                    I knew all along, I had a responsibility to live up to, and I just wasn't holding up my end of the deal.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1500}>
                    Each moment I chose comfort over confrontation—was a moment I defiled my duty.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1600}>
                    While I admit all of that.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1700}>
                    My silence ends today.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1800}>
                    And in thinking about my treasonous actions, I know that my actions from this day forward can redeem me.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1900}>
                    I'm not a religious man, but I hold strong to the words of Christ when he spoke about the blind man.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2000}>
                    "It was not that this man had sinned, nor did his parents sin—that he was born blind. But this man was born blind so that the works of God would be seen through him."
                  </AnimatedParagraph>
                  
                  <div>
                    <div className="my-12 mx-8 md:mx-24 lg:mx-32 px-8 py-6 border-l-4 border-gold bg-gold/5 rounded-r-lg">
                      <blockquote className="text-2xl md:text-3xl font-bold text-foreground leading-tight italic">
                        "Those are powerful words because it reminds me that our wounds are not punishments—they are portals. They are summons to greatness. They are the sacred scars—through which—the Creator unveils miracles."
                      </blockquote>
                    </div>
                  </div>
                  
                  <AnimatedParagraph delay={2200}>
                    Our failings.. our ailments.. our deepest darkness—they are not a curse. They are an invitation. They are crucibles. They are declarations to the world that this man was blind, but now sees; that woman was sick, but now she is whole; that boy was broken, but now, he is invincible.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2300}>
                    This is how God manifests through us—for all to witness.
                  </AnimatedParagraph>

                  <AnimatedParagraph delay={2400}>
                    Since 2006, I have walked a razor's edge, looking for occulted knowledge—that is hidden beneath centuries of what I've come to understand as 'engineered' ignorance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2500}>
                    My baptism by fire— so to speak— was 9/11. That theater of lies didn't just wake me—it sent me into the pits of the reality of the world we live in today.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2600}>
                    One horrifying discovery after another.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2700}>
                    I encountered the great Jordan Maxwell on YouTube in 2007, a prophet of hidden law—who guided me deep into the abyss of maritime admiralty law—America's unseen noose.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2800}>
                    I had first heard about Jordan Maxwell through Michael Tsarion's lecture, 'The Future of Mankind', a presentation so potent with dark realities— it completely annihilated what I thought I knew about the world we live in—and till this very day I remain on the same path of freedom that was invoked in me—when I first became aware of the dire situation we find ourselves in.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2900}>
                    Our natural rights—our divine— unalienable— and sacred rights—are being ritualistically violated. Not by brute force—but by fraudulent legality.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3000}>
                    This legality is 'powered' by our own ignorance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3100}>
                    And I want you to understand this: the word "ignorance" is rooted in "ignore." To ignore is not passive—it is an active war 'against' our own sovereignty.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3200}>
                    The parasites who seek to destroy rely on our ignorance. Their so-called power is only maintained through 'our own refusal' to see— to feel— to act.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3300}>
                    They bleed us—moment by moment, law by law, oath by oath, and contact by contract.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3400}>
                    And this slaughter end with awareness and intelligence.
                  </AnimatedParagraph>
                  
                  <div>
                    <div className="pl-6 my-8 space-y-6">
                      <h4 className="text-xl font-semibold text-gold">The Revolution Begins</h4>
                      
                      <AnimatedParagraph delay={100}>
                        Let it be known that the age of fear and silence is over. For us all.
                      </AnimatedParagraph>
                      
                      <div>
                        <div className="my-12 mx-8 md:mx-24 lg:mx-32 px-8 py-6 border-l-4 border-gold bg-gold/5 rounded-r-lg">
                          <blockquote className="text-2xl md:text-3xl font-bold text-foreground leading-tight italic">
                            "From this moment, I myself vow to speak Truth—not with soft words, but with the flame of divine fury, no matter the consequences."
                          </blockquote>
                        </div>
                      </div>
                      
                      <AnimatedParagraph delay={300}>
                        When we speak truth in this manner, we ignite other sleeping lions.
                      </AnimatedParagraph>
                      
                      <AnimatedParagraph delay={400}>
                        We stand together and shatter every chain forged in ignorance.
                      </AnimatedParagraph>
                      
                      <AnimatedParagraph delay={500}>
                        It's time to stand together in solidarity and work for each other and not for Federal Reserve Notes.
                      </AnimatedParagraph>
                      
                      <AnimatedParagraph delay={600}>
                        This is a spiritual war—and we are the insurgents of light.
                      </AnimatedParagraph>
                    </div>
                  </div>

                  <AnimatedParagraph delay={100}>
                    As Trustee of Freeman & Crown Equity Trust, I do not command a lifeless entity. I command a living, breathing force of lawful rebellion. This trust is not a shelter—it is a sword. It is a vessel of divine agency, armed with moral clarity—guided by sacred duty.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={200}>
                    I claim no perfection except the perfection I inherit through adherence to natural law. I stumble. I bleed. I fuck up.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={300}>
                    But I rise every time.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={400}>
                    And each time I rise—I rise more dangerous, more focused, more responsible, more resilient.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={500}>
                    I expect the same for anyone who claims to walk in Light.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={600}>
                    Let us be keepers of God's Law in our word and in our deed.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={700}>
                    Let us realize our inheritance of abundance—. Let us speak truth no matter how uncomfortable—not to destroy—but to awaken to the reality of our abundance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={800}>
                    The Freeman & Crown Equity Law Group operates as an ever-evolving research project, sharpening our spiritual weapons in the crucible of battle. We adapt. We Test. We Learn. We grow. We ascend. And through our work, we dismantle the ignorance that enslaves us all.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={900}>
                    Our objective is singular and non-negotiable: Unconditional Freedom.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1000}>
                    We do not wrestle against flesh and blood—but against the principalities of ignorance and the thrones of deceit. Our battlefield is unseen by the physical eye. Our enemy wears smiles and titles. Our revolution begins with awareness.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1100}>
                    And so we declare:
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1200}>
                    No. No more deception. No more silence. No more sleepwalking through chains.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1300}>
                    The Revolution begins now. The Revolution begins with You.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1400}>
                    I vow to serve the honor of the beneficiaries, with fierce compassion, with righteous authority, and with openness and transparency.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1500}>
                    The Great Work is underway through this vehicle. Aimed at eradicating ignorance that enslaves us—and in its place—shall rise—a species reborn in truth and light.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1600}>
                    Let every word here be a sword. Let every act contribute to the war against ignorance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1700}>
                    We were blind.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1800}>
                    Now we see.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1900}>
                    <span className="font-medium">Now we act.</span>
                   </AnimatedParagraph>
                 </div>
               </div>
            </div>

            {/* Desktop/Laptop magazine layout */}
            <div className="hidden lg:block max-w-4xl mx-auto">
              <div className="relative">
                {/* Floating image with name and title underneath */}
                <div className="float-left mr-8 mb-6 w-96">
                  <img 
                    src="/lovable-uploads/15868f15-6c06-4358-98e2-9fc153ff4e2c.png" 
                    alt="Adé Abioyé - Trustee" 
                    className="w-full aspect-[3/4] object-cover rounded-lg shadow-lg"
                  />
                  
                  {/* Name and title underneath image */}
                   <div className="mt-4">
                     <div className="mb-1">
                       <p className="text-sm text-muted-foreground mb-1">His Excellency,</p>
                       <h3 className="font-rockwell text-2xl text-primary font-semibold">Prince Adé</h3>
                     </div>
                      <p className="text-base text-muted-foreground font-medium">
                        Attorney-in-fact • Trustee
                      </p>
                   </div>
                </div>
                
                {/* Text content that wraps around the image */}
                <div className="text-lg text-muted-foreground leading-relaxed space-y-6">
                  <div>
                    <div className="pl-6 my-8 space-y-6">
                      <AnimatedParagraph delay={300}>
                        For years after my so-called awakening, I committed what I now consider to be one of the gravest of betrayals:
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={400}>
                        I stayed silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={500}>
                        I had seen with my own eyes— very clearly— the reality that was strategically hidden from the eyes of the masses, yet I stayed silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={600}>
                        I claimed allegiance to the truth, and yet I remained silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={700}>
                        I said I'm a free man of the land and freedom is the birthright of all my people.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={800}>
                        And yet, I remained silent.
                      </AnimatedParagraph>
                      <AnimatedParagraph delay={900}>
                        And in my silence—I became complicit.
                      </AnimatedParagraph>
                    </div>
                  </div>
                  
                  <AnimatedParagraph delay={1000}>
                    I allowed fear to grip my throat and smother the truth that clawed to be set free. Though my eyes had pierced the veil of deception, I remained tethered to fragments of the very illusion I condemned. Bound by the remnants of the programming and the whispers of self-doubt.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1100}>
                    For whatever reason, I let conformity poison my convictions.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1200}>
                    Imagine claiming to have the keys to freedom, only to voluntarily stay locked within the cage of cultural hypnosis—afraid of the responses I might receive from the very hypnotized that I was called to to bring awareness to.
                  </AnimatedParagraph>
                  
                  {/* First Pull Quote */}
                  <div>
                    <div className="my-12 mx-8 md:mx-24 lg:mx-32 px-8 py-6 border-l-4 border-gold bg-gold/5 rounded-r-lg clear-left">
                      <blockquote className="text-2xl md:text-3xl font-bold text-foreground leading-tight italic">
                        "Every day I chose not to speak—was an act of treason—not just against my conscience, but against the divine contract that is written in the deepest part of me."
                      </blockquote>
                    </div>
                  </div>

                  <AnimatedParagraph delay={1400}>
                    I knew all along, I had a responsibility to live up to, and I just wasn't holding up my end of the deal.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1500}>
                    Each moment I chose comfort over confrontation—was a moment I defiled my duty.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1600}>
                    While I admit all of that.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1700}>
                    My silence ends today.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1800}>
                    And in thinking about my treasonous actions, I know that my actions from this day forward can redeem me.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1900}>
                    I'm not a religious man, but I hold strong to the words of Christ when he spoke about the blind man.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2000}>
                    "It was not that this man had sinned, nor did his parents sin—that he was born blind. But this man was born blind so that the works of God would be seen through him."
                  </AnimatedParagraph>
                  
                  <div>
                    <div className="my-12 mx-8 md:mx-24 lg:mx-32 px-8 py-6 border-l-4 border-gold bg-gold/5 rounded-r-lg">
                      <blockquote className="text-2xl md:text-3xl font-bold text-foreground leading-tight italic">
                        "Those are powerful words because it reminds me that our wounds are not punishments—they are portals. They are summons to greatness. They are the sacred scars—through which—the Creator unveils miracles."
                      </blockquote>
                    </div>
                  </div>
                  
                  <AnimatedParagraph delay={2200}>
                    Our failings.. our ailments.. our deepest darkness—they are not a curse. They are an invitation. They are crucibles. They are declarations to the world that this man was blind, but now sees; that woman was sick, but now she is whole; that boy was broken, but now, he is invincible.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2300}>
                    This is how God manifests through us—for all to witness.
                  </AnimatedParagraph>

                  <AnimatedParagraph delay={2400}>
                    Since 2006, I have walked a razor's edge, looking for occulted knowledge—that is hidden beneath centuries of what I've come to understand as 'engineered' ignorance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2500}>
                    My baptism by fire— so to speak— was 9/11. That theater of lies didn't just wake me—it sent me into the pits of the reality of the world we live in today.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2600}>
                    One horrifying discovery after another.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2700}>
                    I encountered the great Jordan Maxwell on YouTube in 2007, a prophet of hidden law—who guided me deep into the abyss of maritime admiralty law—America's unseen noose.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2800}>
                    I had first heard about Jordan Maxwell through Michael Tsarion's lecture, 'The Future of Mankind', a presentation so potent with dark realities— it completely annihilated what I thought I knew about the world we live in—and till this very day I remain on the same path of freedom that was invoked in me—when I first became aware of the dire situation we find ourselves in.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={2900}>
                    Our natural rights—our divine— unalienable— and sacred rights—are being ritualistically violated. Not by brute force—but by fraudulent legality.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3000}>
                    This legality is 'powered' by our own ignorance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3100}>
                    And I want you to understand this: the word "ignorance" is rooted in "ignore." To ignore is not passive—it is an active war 'against' our own sovereignty.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3200}>
                    The parasites who seek to destroy rely on our ignorance. Their so-called power is only maintained through 'our own refusal' to see— to feel— to act.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3300}>
                    They bleed us—moment by moment, law by law, oath by oath, and contact by contract.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={3400}>
                    And this slaughter end with awareness and intelligence.
                  </AnimatedParagraph>
                  
                  <div>
                    <div className="pl-6 my-8 space-y-6 clear-left">
                      <h4 className="text-xl font-semibold text-gold">The Revolution Begins</h4>
                      
                      <AnimatedParagraph delay={100}>
                        Let it be known that the age of fear and silence is over. For us all.
                      </AnimatedParagraph>
                      
                      <div>
                        <div className="my-12 mx-8 md:mx-24 lg:mx-32 px-8 py-6 border-l-4 border-gold bg-gold/5 rounded-r-lg">
                          <blockquote className="text-2xl md:text-3xl font-bold text-foreground leading-tight italic">
                            "From this moment, I myself vow to speak Truth—not with soft words, but with the flame of divine fury, no matter the consequences."
                          </blockquote>
                        </div>
                      </div>
                      
                      <AnimatedParagraph delay={300}>
                        When we speak truth in this manner, we ignite other sleeping lions.
                      </AnimatedParagraph>
                      
                      <AnimatedParagraph delay={400}>
                        We stand together and shatter every chain forged in ignorance.
                      </AnimatedParagraph>
                      
                      <AnimatedParagraph delay={500}>
                        It's time to stand together in solidarity and work for each other and not for Federal Reserve Notes.
                      </AnimatedParagraph>
                      
                      <AnimatedParagraph delay={600}>
                        This is a spiritual war—and we are the insurgents of light.
                      </AnimatedParagraph>
                    </div>
                  </div>

                  <AnimatedParagraph delay={100}>
                    As Trustee of Freeman & Crown Equity Trust, I do not command a lifeless entity. I command a living, breathing force of lawful rebellion. This trust is not a shelter—it is a sword. It is a vessel of divine agency, armed with moral clarity—guided by sacred duty.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={200}>
                    I claim no perfection except the perfection I inherit through adherence to natural law. I stumble. I bleed. I fuck up.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={300}>
                    But I rise every time.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={400}>
                    And each time I rise—I rise more dangerous, more focused, more responsible, more resilient.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={500}>
                    I expect the same for anyone who claims to walk in Light.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={600}>
                    Let us be keepers of God's Law in our word and in our deed.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={700}>
                    Let us realize our inheritance of abundance—. Let us speak truth no matter how uncomfortable—not to destroy—but to awaken to the reality of our abundance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={800}>
                    The Freeman & Crown Equity Law Group operates as an ever-evolving research project, sharpening our spiritual weapons in the crucible of battle. We adapt. We Test. We Learn. We grow. We ascend. And through our work, we dismantle the ignorance that enslaves us all.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={900}>
                    Our objective is singular and non-negotiable: Unconditional Freedom.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1000}>
                    We do not wrestle against flesh and blood—but against the principalities of ignorance and the thrones of deceit. Our battlefield is unseen by the physical eye. Our enemy wears smiles and titles. Our revolution begins with awareness.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1100}>
                    And so we declare:
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1200}>
                    No. No more deception. No more silence. No more sleepwalking through chains.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1300}>
                    The Revolution begins now. The Revolution begins with You.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1400}>
                    I vow to serve the honor of the beneficiaries, with fierce compassion, with righteous authority, and with openness and transparency.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1500}>
                    The Great Work is underway through this vehicle. Aimed at eradicating ignorance that enslaves us—and in its place—shall rise—a species reborn in truth and light.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1600}>
                    Let every word here be a sword. Let every act contribute to the war against ignorance.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1700}>
                    We were blind.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1800}>
                    Now we see.
                  </AnimatedParagraph>
                  
                  <AnimatedParagraph delay={1900}>
                    <span className="font-medium">Now we act.</span>
                  </AnimatedParagraph>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;